<script setup>
import {computed, inject, toRef, toRaw, ref, watch, nextTick} from 'vue';
import {Field, useField} from 'vee-validate';
import prettyBytes from 'pretty-bytes';
import {mixed} from 'yup';
import {defaultFieldProps} from '../helpers/defaultProps';
import {useDccsField} from '../composables/useDccsField';
import {useI18n} from 'vue-i18n';
import {useDebug} from '../../../composables/useDebug';

const {log, error} = useDebug();

const {t} = useI18n();

const props = defineProps({
    ...defaultFieldProps,

    accept: {
        type: String,
        default: null,
    },

    maxFileSize: {
        type: [String, Number],
        default: null,
    },

    isMultiple: {
        type: [Boolean, String],
        default: false,
    },
});

const busy = inject('busy');

const {
    normalizedRequired,
    labelText,
    normalizedAccept,
    normalizedAcceptString,
    normalizedMaxFilesize,
    normalizedIsMultiple,
} = useDccsField(props);

const validationRules = computed(() => {
    let validator = mixed();

    if (normalizedMaxFilesize.value !== null) {
        validator = validator.test('fileSize', '', files => {
            let valid = true;
            if (toRaw(files)) {
                toRaw(files).map(file => {
                    const size = file.size / 1024;
                    if (size > normalizedMaxFilesize.value) {
                        valid = false;
                    }
                });
            }
            return valid;
        });
    }

    if (normalizedAccept.value !== null) {
        validator = validator.test('fileType', '', files => {
            let valid = true;
            if (toRaw(files)) {
                toRaw(files).map(file => {
                    if (!normalizedAccept.value.includes(file.type)) {
                        valid = false;
                    }
                });
            }
            return valid;
        });
    }

    if (normalizedRequired.value === true) {
        validator = validator.required("");
    }

    return validator;
});

const refName = toRef(props, 'name');
const {errorMessage, value} = useField(refName, validationRules, {
    validateOnChange: true,
});
const files = ref([]);

async function parseFile(file) {
    const reader = new FileReader();
    reader.readAsText(file);
    const result = await new Promise((resolve, reject) => {
        reader.onload = function (event) {
            resolve(reader.result)
        }
    })

    files.value.push({
        name: props.name,
        fileName: value.value.name,
        fileExtension: value.value.name.split('.').pop(),
        fileSize: prettyBytes(value.value.size),
        /*TODO MATHIAS: check if we need line below*/
        fileData: reader.result,
    });
}

watch(value, () => {
    nextTick(() => {
        files.value = [];
        if (!normalizedIsMultiple.value && value.value) {
            parseFile(value.value);
        }
    });
});

const uniqueId = (name) =>{
    let uniqueName = name;
    if(uniqueName) {
        uniqueName = uniqueName.toLowerCase().replace(/[^\x00-\x7F]/g, "").replace(/\s/g, "-");
        return uniqueName;
    }

    return "";
}
</script>

<template>
    <Field v-slot="{ field, meta, errors, handleChange, handleBlur }" as="div" :name="name" :label="label"
           class="merkur-field merkur-file-field" :rules="validationRules">
        <label v-if="label && label.length" :for="uniqueId(name)"
               :class="['merkur-label active ', { 'has-error is-danger': errors.length }]">
            {{ placeholder || labelText }}<template v-if="normalizedRequired">*</template>
        </label>
        <input
            :class="['merkur-input ', { 'has-error is-danger': errors.length }]"
            type="file"
            :name="name"
            :multiple="normalizedIsMultiple"
            :required="normalizedRequired"
            :accept="normalizedAcceptString"
            @change="handleChange"
            @blur="handleBlur"
            :disabled="busy"
            :id="uniqueId(name)"
        />
        <div v-if="files.length" class="merkur-files">
            <ul class="merkur-files-list">
                <li v-for="(file, index) in files" :key="`${name}-file-${index}`" class="merkur-file-list-entry">
                    <span class="merkur-file-list-name">
                        {{ file.fileName }}
                    </span>
                    <span class="dccs-file-list-meta">
                        {{ file.fileSize }}
                    </span>
                </li>
            </ul>
        </div>
    </Field>
</template>
